import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Checkbox from "./Checkbox";
import { CURRENT_LIMIT_DISPLAY_VALUES, OP_MODE } from "../../utils/constants";
import "./UnitsListViewComponent.css";
import { useIsWriteRestrictedUser } from "../../hooks/useIsWriteRestrictedUser";
import { unitListReturnState } from "../../utils/unitListReturn";
import Button from "./Button";
import AlertIcon from "./AlertIcon";
import ConnectionStatusIcon from "./ConnectionStatusIcon";
import { SORT_DIRECTION, columnKeyToSortBy, sortByToColumnKey } from "../../utils/unitListQuery";

const buildLocationString = (metadata) =>
  [
    metadata?.propertyName,
    metadata?.buildingName,
    metadata?.unit ? `${metadata.unit}` : null,
    metadata?.floor ? `Floor ${metadata.floor}` : null,
    metadata?.room ? `Room ${metadata.room}` : null,
  ]
    .filter(Boolean)
    .join(", ");

const AlertsCell = ({ activeAlerts, serialNumber }) => {
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const [tooltipDirection, setTooltipDirection] = useState("down");
  const alerts = Array.isArray(activeAlerts) ? activeAlerts : [];
  const hasAlerts = alerts.length > 0;
  const severity = alerts.reduce((acc, alert) => {
    if (alert?.severity === "critical") {
      return "critical";
    }
    if (alert?.severity === "warning" && acc !== "critical") {
      return "warning";
    }
    return acc;
  }, "maintenance");

  const handleTooltipPosition = () => {
    if (!iconRef.current || !tooltipRef.current) return;

    const iconRect = iconRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const spaceAbove = iconRect.top;
    const spaceBelow = window.innerHeight - iconRect.bottom;

    if (spaceBelow < tooltipHeight + 12 && spaceAbove > spaceBelow) {
      setTooltipDirection("up");
      return;
    }

    setTooltipDirection("down");
  };

  if (!hasAlerts) {
    return <span>--</span>;
  }

  return (
    <div className="tooltipHolder relative group w-fit" onMouseEnter={handleTooltipPosition}>
     <AlertIcon severity={severity} data-testid={`alert-icon-${serialNumber}`} />
      <div
        ref={tooltipRef}
        className={`tooltip bg-black rounded-md text-white text-sm px-2 py-1 absolute left-0 z-10 w-72 whitespace-normal invisible opacity-0 group-hover:visible group-hover:opacity-100 ${tooltipDirection === "up" ? "bottom-7" : "top-7"}`}
        data-testid={`unit-${serialNumber}-alert-tooltip`}
      >
        {alerts.map((alert, index) => (
          <div
            key={`${alert?.type || "alert"}-${index}`}
            className={index === alerts.length - 1 ? "" : "border-b border-white/20 pb-2 mb-2"}
          >
            <p><span className="font-semibold">Type:</span> {alert?.type || "--"}</p>
            <p><span className="font-semibold">Severity:</span> {alert?.severity || "--"}</p>
            <p><span className="font-semibold">Message:</span> {alert?.message || "--"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusIconsCell = ({ status, serialNumber }) => (
  <div className="flex items-center gap-4 lg:gap-6">
    <ConnectionStatusIcon
      connected={status?.connected === true}
      connectionType={status?.connectionType}
      className="size-4 lg:size-6"
      data-testid={`connection-status-${serialNumber}`}
    />
    {status?.running === true ? (
      <div className="group relative">
        <div className="powerHolder">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M12 2V6" stroke="#00A653" strokeWidth="1.5" strokeLinecap="round" />
            <path
              d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
              stroke="#00A653"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Power On
        </span>
      </div>
    ) : status?.running === false ? (
      <div className="group relative">
        <div className="powerHolder">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M12 2V6" stroke="#AFC3BC" strokeWidth="1.5" strokeLinecap="round" />
            <path
              d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
              stroke="#AFC3BC"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Power Off
        </span>
      </div>
    ) : (
      <div className="group relative">
        <div className="powerHolder">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M12 2V6" stroke="#AFC3BC" strokeWidth="1.5" strokeLinecap="round" />
            <path
              d="M8.5 3.70605C5.26806 5.07157 3 8.27099 3 12.0001C3 16.9707 7.02944 21.0001 12 21.0001C16.9706 21.0001 21 16.9707 21 12.0001C21 8.27099 18.7319 5.07157 15.5 3.70605"
              stroke="#AFC3BC"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Power Unknown
        </span>
      </div>
    )}

    <div className="tooltipHolder flex items-center gap-2 relative group">
      {status?.mode === OP_MODE.DRY && (
        <>
          <div className="dryHolder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 41 40"
              fill="none"
            >
              <path
                d="M20.4799 4.16699C18.5078 6.23898 11.7324 13.6955 11.7324 19.7451C11.7324 24.5775 15.6474 28.4951 20.4799 28.4951C25.3124 28.4951 29.2299 24.5775 29.2299 19.7451C29.2299 13.6955 22.452 6.23898 20.4799 4.16699Z"
                stroke="#3B82F6"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3965 31.6621L27.5632 8.32878"
                stroke="#F87171"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
            Dry
          </span>
        </>
      )}

      {status?.mode === OP_MODE.FAN_ONLY && (
        <>
          <div className="fanHolder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3 8H9.5C10.8807 8 12 6.88071 12 5.5C12 4.11929 10.8807 3 9.5 3C8.11929 3 7 4.11929 7 5.5V5.85714"
                stroke="#00BBDD"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M4 14H18.5C20.433 14 22 15.567 22 17.5C22 19.433 20.433 21 18.5 21C16.567 21 15 19.433 15 17.5V17"
                stroke="#00BBDD"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M2 11H18.5C20.433 11 22 9.433 22 7.5C22 5.567 20.433 4 18.5 4C16.567 4 15 5.567 15 7.5V8"
                stroke="#00BBDD"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
            Fan
          </span>
        </>
      )}

      {status?.mode === OP_MODE.HEAT && (
        <>
          <div className="heatHolder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle cx="12" cy="12" r="5" stroke="#FE5B26" strokeWidth="1.5" />
              <path d="M12 2V4" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 20V22" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M4 12L2 12" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M22 12L20 12" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path
                d="M19.7778 4.22266L17.5558 6.25424"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M4.22217 4.22266L6.44418 6.25424"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M6.44434 17.5557L4.22211 19.7779"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M19.7778 19.7773L17.5558 17.5551"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
            Heat
          </span>
        </>
      )}

      {status?.mode === OP_MODE.COOL && (
        <>
          <div className="coolHolder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2V18M12 22V18M12 18L15 21M12 18L9 21M15 3L12 6L9 3"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M3.33978 7.00042L6.80389 9.00042M6.80389 9.00042L17.1962 15.0004M6.80389 9.00042L5.70581 4.90234M6.80389 9.00042L2.70581 10.0985M17.1962 15.0004L20.6603 17.0004M17.1962 15.0004L21.2943 13.9023M17.1962 15.0004L18.2943 19.0985"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M20.6602 7.00042L17.1961 9.00042M17.1961 9.00042L6.80376 15.0004M17.1961 9.00042L18.2941 4.90234M17.1961 9.00042L21.2941 10.0985M6.80376 15.0004L3.33966 17.0004M6.80376 15.0004L2.70569 13.9023M6.80376 15.0004L5.70569 19.0985"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
            Cool
          </span>
        </>
      )}

      {status?.mode === OP_MODE.HEAT_AND_COOL && (
        <>
          <div className="heatCoolHolder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                d="M14 2V18M14 22V18M14 18L17 21M14 6L17 3"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M19.1963 15L22.6604 17M19.1963 15L23.2944 13.9019M19.1963 15L20.2944 19.0981M19.1963 15L14.0001 12"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M22.6601 7.00042L19.196 9.00042M19.196 9.00042L20.2941 4.90234M19.196 9.00042L23.2941 10.0985M19.196 9.00042L13.9999 12.0004"
                stroke="#0099FF"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M11 12C11 14.7614 11 14.5 11 17C8.23858 17 6 14.7614 6 12C6 9.23858 8.23858 7 11 7C11 8.5 11 9.23858 11 12Z"
                stroke="#FE5B26"
                strokeWidth="1.5"
              />
              <path d="M11 2V4" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M11 20V22" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M3 12L1 12" stroke="#FE5B26" strokeWidth="1.5" strokeLinecap="round" />
              <path
                d="M3.22217 4.22266L5.44418 6.25424"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M5.44434 17.5557L3.22211 19.7779"
                stroke="#FE5B26"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
            Heat and Cool
          </span>
        </>
      )}
    </div>
  </div>
);

const ActionsCell = ({
  row,
  activeDropdownId,
  setActiveDropdownId,
  setSelectedUnits,
  handleEditUnit,
  handleRemoveUnit,
  handleGenerateReport,
  dropdownRefs,
  isActionsDisabled,
}) => (
  <div className="actionHolder size-4 lg:size-6 relative">
    <Button
      data-testid={`unit-${row.serialNumber}-actions-toggle`}
      onClick={() => {
        setSelectedUnits(row);
        setActiveDropdownId((prevSerialNumber) =>
          row.serialNumber === prevSerialNumber ? null : row.serialNumber
        );
      }}
      className="flex items-center gap-2 text-primaryText font-semibold text-sm leading-6 tracking-[0.021px]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="size-full"
      >
        <circle cx="5" cy="12" r="2" fill="#FF8C65" />
        <circle cx="12" cy="12" r="2" fill="#FF8C65" />
        <circle cx="19" cy="12" r="2" fill="#FF8C65" />
      </svg>
    </Button>
    {row.serialNumber === activeDropdownId && (
      <div
        className={`dropdownContent min-w-24 text-nowrap absolute right-2.5 bg-[#FFF4D1] dark:bg-darkThemeDropdownBg dark:shadow-darkThemeDropdownMenuShadow rounded-lg shadow-appShadow cursor-pointer z-50 ${row.rowIndex < 2 ? "top-full" : "bottom-full"}`}
        ref={(el) => {
          if (el) {
            dropdownRefs.current[row.serialNumber] = el;
          } else {
            delete dropdownRefs.current[row.serialNumber];
          }
        }}
        data-testid={`unit-${row.serialNumber}-actions-menu`}
      >
        <ul className="flex flex-col">
          <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
            <Button
              data-testid={`unit-${row.serialNumber}-edit`}
              onClick={() => handleEditUnit(row)}
              className={`w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded ${
                isActionsDisabled ? "cursor-not-allowed opacity-50" : ""
              }`}
              disabled={isActionsDisabled}
            >
              Edit
            </Button>
          </li>
          {handleGenerateReport && (
            <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
              <Button
                data-testid={`unit-${row.serialNumber}-generate-report`}
                onClick={() => handleGenerateReport(row)}
                className="w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded"
              >
                Generate Report
              </Button>
            </li>
          )}
          <li className="border-b border-grey dark:border-darkThemeBorder last:border-0">
            <Button
              data-testid={`unit-${row.serialNumber}-remove-from-zone`}
              onClick={() => handleRemoveUnit(row)}
              className={`w-full text-left text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded ${
                isActionsDisabled ? "cursor-not-allowed opacity-50" : ""
              }`}
              disabled={isActionsDisabled}
            >
              Remove from zone
            </Button>
          </li>
        </ul>
      </div>
    )}
  </div>
);

const SetpointCell = ({ status }) => {
  if (!status?.running || status?.mode === OP_MODE.FAN_ONLY) {
    return <p>--</p>;
  }

  if (status?.mode === OP_MODE.HEAT) {
    return (
      <>
        <span className="text-[#FE5B26]">
          {status?.heatTargetTempF ? `${status.heatTargetTempF}°F` : "--"}
        </span>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Heat Setpoint
        </span>
      </>
    );
  }

  if (status?.mode === OP_MODE.COOL) {
    return (
      <>
        <span className="text-[#0099FF]">
          {status?.coolTargetTempF ? `${status.coolTargetTempF}°F` : "--"}
        </span>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Cool Setpoint
        </span>
      </>
    );
  }

  if (status?.mode === OP_MODE.HEAT_AND_COOL) {
    return (
      <>
        <span className="text-[#FE5B26]">
          {status?.heatTargetTempF ? `${status.heatTargetTempF}°F` : "--"}
        </span>
        <span className="text-[#0099FF]">
          {status?.coolTargetTempF ? `${status.coolTargetTempF}°F` : "--"}
        </span>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Heat &amp; Cool Setpoint
        </span>
      </>
    );
  }

  if (status?.mode === OP_MODE.DRY) {
    return (
      <>
        <span className="text-[#0099FF]">
          {status?.coolTargetTempF ? `${status.coolTargetTempF}°F` : "--"}
        </span>
        <span className="tooltip bg-black rounded-md text-white text-xs whitespace-nowrap hidden group-hover:inline-block px-2 py-1 absolute bottom-8">
          Cool Setpoint
        </span>
      </>
    );
  }

  return <p>--</p>;
};

const SortableHeaderCell = ({ label, sortDirection }) => (
  <div className="units-sort-header">
    <span>{label}</span>
    <span className="units-sort-icons" aria-hidden>
      <span className={`units-sort-icon up ${sortDirection === SORT_DIRECTION.ASC ? "active" : ""}`} />
      <span className={`units-sort-icon down ${sortDirection === SORT_DIRECTION.DESC ? "active" : ""}`} />
    </span>
  </div>
);

const sortableHeader = (label) => ({ column }) => {
  const sortDirection = column.getIsSorted() === "asc"
    ? SORT_DIRECTION.ASC
    : column.getIsSorted() === "desc"
      ? SORT_DIRECTION.DESC
      : "";
  return <SortableHeaderCell label={label} sortDirection={sortDirection} />;
};

const UnitsListViewComponent = ({
  units,
  fleet,
  activeDropdownId,
  setActiveDropdownId,
  setSelectedUnits,
  handleRemoveUnit,
  handleEditUnit,
  selectedUnitsForBulkAction,
  addUnitsToSelectedUnitsForBulkAction,
  removeUnitsFromSelectedUnitsForBulkAction,
  selectedSortBy,
  selectedOrder,
  onSortChange,
  handleGenerateReport,
}) => {
  const isActionsDisabled = useIsWriteRestrictedUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [fullPageSelectionState, setFullPageSelectionState] = useState("NOT_SELECTED"); // NOT_SELECTED, PARTIALLY_SELECTED, FULLY_SELECTED

  const handleFullPageBulkActionCheckboxClick = useCallback(
    (checked) => {
      if (checked) {
        addUnitsToSelectedUnitsForBulkAction(units.map((unit) => ({ ...unit, zone: fleet })));
      } else {
        removeUnitsFromSelectedUnitsForBulkAction(units.map((unit) => ({ ...unit, zone: fleet })));
      }
    },
    [addUnitsToSelectedUnitsForBulkAction, fleet, removeUnitsFromSelectedUnitsForBulkAction, units]
  );

  useEffect(() => {
    const selectedCount = units.filter((unit) => !!selectedUnitsForBulkAction[unit.serialNumber]).length;
    if (units.length === selectedCount && selectedCount > 0) {
      setFullPageSelectionState("FULLY_SELECTED");
    } else if (selectedCount === 0) {
      setFullPageSelectionState("NOT_SELECTED");
    } else if (fullPageSelectionState === "FULLY_SELECTED") {
      setFullPageSelectionState("PARTIALLY_SELECTED");
    }
  }, [units, fullPageSelectionState, selectedUnitsForBulkAction, setFullPageSelectionState]);

  const dropdownRefs = useRef({});
  const [columnSizing, setColumnSizing] = useState(() => {
    try {
      const stored = localStorage.getItem("unitsListColumnWidths");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = Object.values(dropdownRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );

      if (isOutside) {
        setActiveDropdownId(null);
      }
    };

    if (activeDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdownId, setActiveDropdownId]);

  const rows = useMemo(
    () =>
      units.map((unit, index) => ({
        ...unit,
        rowIndex: index,
        locationString: buildLocationString(unit?.metadata || {}),
      })),
    [units]
  );

  const sorting = useMemo(() => {
    if (!selectedSortBy || !selectedOrder) return [];
    const columnKey = sortByToColumnKey[selectedSortBy];
    if (!columnKey) return [];
    return [
      {
        id: columnKey,
        desc: selectedOrder === SORT_DIRECTION.DESC,
      },
    ];
  }, [selectedOrder, selectedSortBy]);

  const columns = useMemo(
    () => [
      {
        id: "bulkSelect",
        header: () => (
          <Checkbox
            data-testid="units-select-all-checkbox"
            checked={fullPageSelectionState === "FULLY_SELECTED"}
            indeterminate={fullPageSelectionState === "PARTIALLY_SELECTED"}
            onChange={handleFullPageBulkActionCheckboxClick}
          />
        ),
        size: 75,
        enableResizing: false,
        cell: ({ row }) => (
          <Checkbox
            data-testid={`unit-${row.original.serialNumber}-checkbox`}
            checked={!!selectedUnitsForBulkAction[row.original.serialNumber]}
            onChange={(checked) => {
              if (checked) {
                addUnitsToSelectedUnitsForBulkAction([{ ...row.original, zone: fleet }]);
              } else {
                removeUnitsFromSelectedUnitsForBulkAction([{ ...row.original, zone: fleet }]);
              }
            }}
            aria-label={`Select unit ${row.original.serialNumber}`}
          />
        ),
      },
      {
        id: "serialNumber",
        accessorKey: "serialNumber",
        header: sortableHeader("Serial Number"),
        size: 180,
        enableSorting: true,
        enableResizing: true,
        cell: ({ row }) => (
          <span
            className="text-sm leading-5 lg:text-base lg:leading-6 cursor-pointer hover:underline"
            data-testid={`unit-${row.original.serialNumber}-serial-link`}
            onClick={() => navigate(`/units/${row.original.serialNumber}`, { state: unitListReturnState("units", location.search) })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(`/units/${row.original.serialNumber}`, { state: unitListReturnState("units", location.search) });
              }
            }}
            role="button"
            tabIndex={0}
          >
            {row.original.serialNumber}
          </span>
        ),
      },
      {
        id: "name",
        accessorFn: (row) => row?.metadata?.name || "--",
        header: sortableHeader("Unit Name"),
        size: 120,
        enableSorting: true,
        sortDescFirst: false,
        enableResizing: true,
        cell: ({ row }) => (
          <p className="w-full units-cell-wrap" title={row.original?.metadata?.name}>
            {row.original?.metadata?.name}
          </p>
        ),
      },
     {
        id: "location",
        header: "Location",
        size: 240,
        enableResizing: true,
        cell: ({ row }) => (
          <p className="w-full" title={row.original.locationString}>
            {row.original.locationString}
          </p>
        ),
      },
      {
        id: "currentLimit",
        accessorFn: (row) => row?.status?.currentLimit || "",
        header: sortableHeader("Current Limit"),
        size: 180,
        enableResizing: true,
        cell: ({ row }) => (
          <p
            className="w-full"
            title={CURRENT_LIMIT_DISPLAY_VALUES[row.original?.status?.currentLimit] || "--"}
          >
            {CURRENT_LIMIT_DISPLAY_VALUES[row.original?.status?.currentLimit] || "--"}
          </p>
        ),
      },
      {
        id: "alerts",
        accessorFn: (row) => row?.activeAlerts?.alerts?.length || 0,
        header: sortableHeader("Alerts"),
        size: 180,
        enableResizing: true,
        sortDescFirst: false,
        cell: ({ row }) => (
          <AlertsCell
            activeAlerts={row.original?.activeAlerts?.alerts}
            serialNumber={row.original?.serialNumber}
          />
        ),
      },
      {
        id: "controlSource",
        accessorFn: (row) => row?.status?.controlSource || "",
        header: sortableHeader("Control Source"),
        size: 180,
        enableResizing: true,
        cell: ({ row }) => (
          <p className="w-full" title={row.original?.status?.controlSource || "--"}>
            {row.original?.status?.controlSource || "--"}
          </p>
        ),
      },
      {
        id: "scheduleSource",
        accessorFn: (row) => row?.status?.scheduleSource || "",
        header: sortableHeader("Schedule Source"),
        size: 180,
        enableResizing: true,
        cell: ({ row }) => (
          <p className="w-full" title={row.original?.status?.scheduleSource || "--"}>
            {row.original?.status?.scheduleSource || "--"}
          </p>
        ),
      },
      {
        id: "unitStatus",
        header: "Unit Status",
        size: 220,
        enableResizing: true,
        cell: ({ row }) => (
          <StatusIconsCell
            status={row.original?.status}
            serialNumber={row.original?.serialNumber}
          />
        ),
      },
      {
        id: "indoorTemperature",
        accessorFn: (row) => row?.status?.indoorTempF,
        header: sortableHeader("Indoor Temperature"),
        size: 180,
        enableResizing: true,
        sortDescFirst: false,
        cell: ({ row }) => (
          <span>{row.original?.status?.indoorTempF ? `${row.original.status.indoorTempF}°F` : "--"}</span>
        ),
      },
      {
        id: "setpoint",
        accessorFn: (row) => row?.status?.coolTargetTempF ?? row?.status?.heatTargetTempF,
        header: sortableHeader("Setpoint"),
        size: 180,
        enableResizing: true,
        sortDescFirst: false,
        cell: ({ row }) => (
          <div className="tooltipHolder flex items-center gap-2 relative group">
            <SetpointCell status={row.original?.status} />
          </div>
        ),
      },
      {
        id: "actions",
        header: null,
        size: 180,
        enableResizing: false,
        meta: { cellClassName: "allow-overflow" },
        cell: ({ row }) => (
          <ActionsCell
            row={row.original}
            activeDropdownId={activeDropdownId}
            setActiveDropdownId={setActiveDropdownId}
            setSelectedUnits={setSelectedUnits}
            handleEditUnit={handleEditUnit}
            handleRemoveUnit={handleRemoveUnit}
            dropdownRefs={dropdownRefs}
            handleGenerateReport={handleGenerateReport}
            isActionsDisabled={isActionsDisabled}
          />
        ),
      },
    ],
    [
      activeDropdownId,
      isActionsDisabled,
      addUnitsToSelectedUnitsForBulkAction,
      fullPageSelectionState,
      removeUnitsFromSelectedUnitsForBulkAction,
      handleFullPageBulkActionCheckboxClick,
      handleEditUnit,
      handleRemoveUnit,
      selectedUnitsForBulkAction,
      setActiveDropdownId,
      setSelectedUnits,
      fleet,
      handleGenerateReport,
      navigate,
      location.search,
    ]
  );

  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing((prev) => {
      const updated = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem("unitsListColumnWidths", JSON.stringify(updated));
      } catch (error) {
        // ignore write failures
      }
      return updated;
    });
  }, []);

  const handleSortingChange = useCallback(
    (updater) => {
      if (!onSortChange) return;
      const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
      const [nextSort] = nextSorting;

      if (!nextSort) {
        onSortChange({ sortBy: "", order: "" });
        return;
      }

      onSortChange({
        sortBy: columnKeyToSortBy[nextSort.id] || "",
        order: nextSort.desc ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
      });
    },
    [onSortChange, sorting]
  );

  const gridHeight = useMemo(
    () => Math.max(360, Math.min(900, (rows.length || 1) * 78 + 64)),
    [rows.length]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnSizing, sorting },
    onColumnSizingChange: handleColumnSizingChange,
    onSortingChange: handleSortingChange,
    columnResizeMode: "onChange",
    manualSorting: true,
    getRowId: (row) => row.serialNumber,
  });

  return (
    <div
      className="mt-4 unitsListHolder rounded-md scrollVisible overflow-x-auto w-full"
      data-testid="units-list-view"
    >
      <div className="units-data-grid" style={{ height: gridHeight, width: "100%" }}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} style={{ height: 54 }}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="units-table-header-cell"
                    style={{ width: header.getSize() }}
                  >
                    <div
                      className={`units-table-header-content ${
                        header.column.getCanSort() ? "sortable" : ""
                      }`}
                      data-testid={header.column.getCanSort() ? `sort-header-${header.id}` : undefined}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      onKeyDown={
                        header.column.getCanSort()
                          ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                header.column.toggleSorting();
                              }
                            }
                          : undefined
                      }
                      role={header.column.getCanSort() ? "button" : undefined}
                      tabIndex={header.column.getCanSort() ? 0 : undefined}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        className={`column-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onTouchStart={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="units-table-row"
                style={{ height: 76 }}
                data-testid={`unit-row-${row.original.serialNumber}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`units-table-cell ${cell.column.columnDef.meta?.cellClassName || ""}`}
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnitsListViewComponent;
